import { useEffect, useMemo, useRef, useState } from "react";
import { Filter as FilterIcon } from "lucide-react";
import { fmtTL } from "../lib/format";

const INPUT = "w-full px-2 py-1 text-xs border border-slate-300 rounded";

// açılış konumu: altta yer yoksa ve üstte daha çok yer varsa yukarı açılır; sağa/sola hizalanır
function panelStyle(anchor, align) {
  const r = anchor.getBoundingClientRect();
  const gap = 6;
  const spaceBelow = window.innerHeight - r.bottom - gap;
  const openUp = spaceBelow < 300 && r.top > spaceBelow;
  const s = { position: "fixed", zIndex: 60, overflowY: "auto" };
  if (openUp) { s.bottom = window.innerHeight - r.top + gap; s.maxHeight = Math.max(160, r.top - gap * 2); }
  else { s.top = r.bottom + gap; s.maxHeight = Math.max(160, spaceBelow - gap); }
  if (align === "right") s.right = Math.max(8, window.innerWidth - r.right);
  else s.left = Math.max(8, Math.min(r.left, window.innerWidth - 410));
  return s;
}

// açık panel viewport dışına taştıysa yukarı çekilir
function clampStyle(s, rect) {
  const next = { ...s };
  delete next.bottom;
  next.top = Math.max(8, window.innerHeight - rect.height - 8);
  next.maxHeight = window.innerHeight - 16;
  return next;
}

// dışarı tıklama / kaydırma kapatır; açıkken ESC temizleyip kapatır (sayfa seviyesi ESC'yi tetiklemez)
function usePanelClose(ref, open, setOpen, onClear) {
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", onScroll);
    return () => { document.removeEventListener("mousedown", close); window.removeEventListener("scroll", onScroll); };
  }, [ref, setOpen]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      if (onClear) onClear();
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClear, setOpen]);
}

export function ColFilter({ testid, active, align = "left", onClear, children }) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);
  const panelRef = useRef(null);
  usePanelClose(ref, open, setOpen, onClear);
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const r = panelRef.current.getBoundingClientRect();
    if (r.bottom > window.innerHeight - 4 || r.top < 4) setStyle((s) => clampStyle(s, r));
  }, [open]);
  const toggle = () => {
    if (!open && ref.current) setStyle(panelStyle(ref.current, align));
    setOpen(!open);
  };
  return (
    <span className="relative inline-block ml-1 align-middle" ref={ref}>
      <button type="button" data-testid={testid} onClick={toggle} title="Filtrele"
        className={`transition-colors ${active ? "text-amber-600" : "text-slate-400 hover:text-slate-600"}`}>
        <FilterIcon className="w-3 h-3" />
      </button>
      {open && (
        <div ref={panelRef} style={style} className="w-max min-w-64 max-w-96 bg-white border border-slate-200 rounded-md shadow-lg p-2 normal-case tracking-normal text-left" data-testid={`${testid}-panel`}>
          {children}
        </div>
      )}
    </span>
  );
}

export function MultiChecks({ options, values, onChange, testid }) {
  const [q, setQ] = useState("");
  const filtered = options.filter((o) => o.label.toLocaleLowerCase("tr").includes(q.toLocaleLowerCase("tr")));
  const toggle = (v) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <div className="space-y-1.5">
      {options.length > 8 && <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara…" className={INPUT} data-testid={`${testid}-search`} />}
      <div className="max-h-44 overflow-y-auto space-y-1">
        {filtered.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-xs cursor-pointer text-slate-700" data-testid={`${testid}-opt-${o.value}`}>
            <input type="checkbox" checked={values.includes(o.value)} onChange={() => toggle(o.value)} className="accent-amber-600 w-3.5 h-3.5" />
            <span className="truncate">{o.label}</span>
          </label>
        ))}
        {filtered.length === 0 && <div className="text-[11px] text-slate-400 px-1 py-1">Sonuç yok</div>}
      </div>
      {values.length > 0 && <button type="button" onClick={() => onChange([])} className="text-[10px] font-semibold text-red-600 hover:text-red-800" data-testid={`${testid}-clear`}>Temizle ({values.length})</button>}
    </div>
  );
}

export function TextFilter({ value, onChange, testid, placeholder = "Ara…" }) {
  return <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={INPUT} data-testid={testid} />;
}

export function DateRangeFilter({ from, to, onFrom, onTo, testid }) {
  return (
    <div className="space-y-1.5">
      <input type="date" value={from} onChange={(e) => onFrom(e.target.value)} className={INPUT} data-testid={`${testid}-from`} />
      <input type="date" value={to} onChange={(e) => onTo(e.target.value)} className={INPUT} data-testid={`${testid}-to`} />
    </div>
  );
}

function DateMultiFilter({ f, opts, set, testid }) {
  return (
    <div className="space-y-1.5">
      <input type="date" value={f?.day || ""} onChange={(e) => set({ day: e.target.value, values: [] })} className={INPUT} data-testid={`${testid}-day`} />
      <MultiChecks options={opts} values={f?.values || []} onChange={(v) => set({ day: "", values: v })} testid={testid} />
    </div>
  );
}

function NumberRangeFilter({ f, set, testid }) {
  return (
    <div className="flex items-center gap-1">
      <input type="number" value={f?.min ?? ""} onChange={(e) => set({ ...(f || {}), min: e.target.value })} placeholder="Min" className="w-20 px-2 py-1 text-xs border border-slate-300 rounded" data-testid={`${testid}-min`} />
      <input type="number" value={f?.max ?? ""} onChange={(e) => set({ ...(f || {}), max: e.target.value })} placeholder="Max" className="w-20 px-2 py-1 text-xs border border-slate-300 rounded" data-testid={`${testid}-max`} />
    </div>
  );
}

// kolon değeri metin olarak ("—" boş); multiany: dizi değer (örn. şubedeki taşeronlar)
const cellText = (c, it) => String((c.value ? c.value(it) : it[c.key]) ?? "—");
const cellVals = (c, it) => { const v = c.value ? c.value(it) : it[c.key]; return Array.isArray(v) ? v.map(String) : [String(v ?? "—")]; };

const MATCHERS = {
  text: (f, txt) => txt.toLocaleLowerCase("tr").includes(String(f).toLocaleLowerCase("tr")),
  date: (f, txt) => !(f.from && txt < f.from) && !(f.to && txt > f.to),
  datemulti: (f, txt) => (f.day ? txt === f.day : !(f.values || []).length || f.values.includes(txt)),
  number: (f, txt) => { const n = parseFloat(txt.replace(",", ".")) || 0; return !(f.min !== "" && n < parseFloat(f.min)) && !(f.max !== "" && n > parseFloat(f.max)); },
  multi: (f, txt) => !f.length || f.includes(txt),
  multiany: (f, _txt, vals) => !f.length || vals.some((v) => f.includes(v)),
};

const ACTIVE = {
  date: (f) => !!(f && (f.from || f.to || f.min || f.max)),
  number: (f) => !!(f && (f.from || f.to || f.min || f.max)),
  datemulti: (f) => !!(f && (f.day || (f.values || []).length)),
  text: (f) => !!f,
  multi: (f) => !!(f && f.length),
  multiany: (f) => !!(f && f.length),
};

export const isFilterActive = (col, f) => (ACTIVE[col.type] || ACTIVE.multi)(f);

// cols: [{ key, label, type: "multi"|"multiany"|"text"|"date"|"number", value?: (item)=>any, options?: [{value,label}] }]
// type yoksa: options varsa multi; yoksa <=15 tekil değer multi, çoksa text otomatik seçilir
export function useColFilters(items, cols) {
  const [hf, setHf] = useState({});
  const autoOpts = useMemo(() => Object.fromEntries(cols.map((c) => [c.key, [...new Set(items.flatMap((it) => cellVals(c, it)))].sort((a, b) => a.localeCompare(b, "tr"))])), [items, cols]);
  const visible = useMemo(() => items.filter((it) => cols.every((c) => {
    const f = hf[c.key];
    if (f == null || f === "") return true;
    return (MATCHERS[c.type] || MATCHERS.multi)(f, cellText(c, it), cellVals(c, it));
  })), [items, hf, cols]);
  return [hf, setHf, visible, autoOpts];
}

function FilterBody({ effType, f, opts, set, testid }) {
  if (effType === "text") return <TextFilter value={f || ""} onChange={set} testid={`${testid}-input`} />;
  if (effType === "date") return <DateRangeFilter from={f?.from || ""} to={f?.to || ""} onFrom={(v) => set({ ...(f || {}), from: v })} onTo={(v) => set({ ...(f || {}), to: v })} testid={testid} />;
  if (effType === "datemulti") return <DateMultiFilter f={f} opts={opts} set={set} testid={testid} />;
  if (effType === "number") return <NumberRangeFilter f={f} set={set} testid={testid} />;
  return <MultiChecks options={opts} values={f || []} onChange={set} testid={testid} />;
}

export function FilterTh({ col, hf, setHf, testid, align = "left", autoOptions = [], wrap = false }) {
  const f = hf[col.key];
  const active = (ACTIVE[col.type] || ACTIVE.multi)(f);
  const opts = col.options || autoOptions.map((v) => ({ value: v, label: col.fmt ? col.fmt(v) : v }));
  const effType = col.type || (opts.length > 15 ? "text" : "multi");
  const set = (v) => setHf({ ...hf, [col.key]: v });
  const clear = () => { const next = { ...hf }; delete next[col.key]; setHf(next); };
  return (
    <span className={wrap ? "" : "whitespace-nowrap"}>
      {col.label}
      <ColFilter testid={testid} active={active} align={align} onClear={clear}>
        <FilterBody effType={effType} f={f} opts={opts} set={set} testid={testid} />
        {active && (
          <button type="button" onClick={clear} data-testid={`${testid}-clear`}
            className="mt-1.5 w-full px-2 py-1 rounded border border-red-200 text-red-700 text-[11px] font-semibold hover:bg-red-50 transition-colors">
            Filtreyi Temizle
          </button>
        )}
      </ColFilter>
    </span>
  );
}

// sütun filtreli listelerin altında filtrelenmiş satır toplamı (+ opsiyonel son kayıtlar)
export function FilterTotal({ rows, sums = [], testid = "filter-total", recent }) {
  return (
    <>
      {recent && recent.rows?.length > 0 && (
        <div className="border-b border-slate-200" data-testid={`${testid}-recent`}>
          <div className="px-4 py-1.5 bg-slate-100 font-bold text-slate-700 uppercase tracking-wider text-[10px]">Son Kayıtlar</div>
          <table className="w-full text-sm">
            {recent.head && <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr>{recent.head}</tr></thead>}
            <tbody>
              {recent.rows.slice(0, recent.count || 4).map((r, i) => (
                <tr key={r.id || i} className="border-t border-slate-100 bg-white" data-testid={`${testid}-recent-${i}`}>{recent.render(r)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="px-4 py-2 border-b-2 border-amber-300 bg-amber-50/60 text-xs font-semibold text-amber-900 flex items-center gap-4 flex-wrap" data-testid={testid}>
        <span>Filtre Toplamı: {rows.length} kayıt</span>
        {sums.map((s) => (
          <span key={s.key} data-testid={`${testid}-${s.key}`}>
            {s.label}: {fmtTL(rows.reduce((a, r) => a + (Number(r[s.key]) || 0), 0))}
          </span>
        ))}
      </div>
    </>
  );
}
